import { useEffect, useMemo, useRef, useState } from 'react'
import { useData } from '../../core/contexts/DataContext'
import { Exhibitor } from '../../core/types/Exhibitor.model';
import { SalesPerson } from '../../core/types/SalesPerson.model';
import { Meeting, MeetingStatus } from '../../core/types/Meeting.model';
import SalesPersonsPreview from './components/SalesPersonsPreview';
import MeetingTimes from './components/MeetingTimes';
import ExhibitorsPreview from './components/ExhibitorsPreview';
import { useAuth } from '../../core/contexts/AuthContext';
import { formatTime } from '../../utils/dateUtils';
import ConformSlotPopup from './components/ConformSlotPopup';


function getMeetingDateStr(startTime: string): string {
  const date = new Date(startTime);
  return date.toDateString();
}

const SchedulingMeeting = () => {
  const { exhibitors, exhibitorsMeetingsDates, bookMeeting, isLoaded, logosMap, getExhibitorDetails, lockMeeting, unlockMeeting } = useData();
  const [selectedExhibitor, setSelectedExhibitor] = useState<Exhibitor | null>(null);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<SalesPerson | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [filteredSlots, setFilteredSlots] = useState<Meeting[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Meeting | null>(null);
  const [filterPhrase, setFilterPhrase] = useState("");
  const [filterOlyWithMeetings, setFilterOnlyWithMeetings] = useState(false);
  const { user } = useAuth();

  const selectExhibitorRef = useRef<HTMLDivElement>(null);
  const selectSalesPersonRef = useRef<HTMLDivElement>(null);
  const selectSlotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (exhibitorsMeetingsDates.includes(selectedDate)) return;
    setSelectedDate(exhibitorsMeetingsDates[0] || '');
  }, [exhibitorsMeetingsDates]);



  useEffect(() => { setSelectedExhibitor(null); setSelectedSalesPerson(null); }, [filterPhrase, filterOlyWithMeetings]);

  useEffect(() => {
    if (!selectedSalesPerson || !selectedSalesPerson.meetings) {
      setFilteredSlots([]);
      return;
    };
    setFilteredSlots(selectedSalesPerson.meetings.filter(
      (slot) => getMeetingDateStr(slot.startTime) === selectedDate
    ));
    setSelectedSlot(null);
  }, [selectedSalesPerson, selectedDate]);


  useEffect(() => {
    if (!selectedSlot) return;

    if (selectedExhibitor) getExhibitorDetails(selectedExhibitor!);
  }, [selectedSlot]);

  useEffect(() => {
    if (selectedExhibitor && selectSalesPersonRef.current) {
      selectSalesPersonRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedExhibitor]);

  useEffect(() => {
    if (selectedSalesPerson && selectSlotRef.current) {
      selectSlotRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedSalesPerson]);

  const filteredExhibitors = useMemo(() => {
    return exhibitors.filter(exhibitor => {
      const matchesFilterPhrase = exhibitor.origName.toLowerCase().includes(filterPhrase.toLowerCase()) ||
        exhibitor.boothNo.toLowerCase().includes(filterPhrase.toLowerCase()) ||
        exhibitor.origName.toLowerCase().includes(filterPhrase.toLowerCase()) ||
        exhibitor.salesPersons.some(sp => sp.fullName.toLowerCase().includes(filterPhrase.toLowerCase()));

      const matchesOlyWithMeetings = !filterOlyWithMeetings || exhibitor.salesPersons.some(salesPerson =>
        salesPerson?.meetings?.some(slot => slot.status === "Available" && getMeetingDateStr(slot.startTime) === selectedDate)
      );

      return matchesFilterPhrase && matchesOlyWithMeetings;
    });
  }, [exhibitors, filterPhrase, filterOlyWithMeetings, selectedDate]);


  const handleExhibitorSelect = (exhibitor: Exhibitor) => {
    setSelectedExhibitor(exhibitor)
    setSelectedSalesPerson(null)
  }

  const handleSalesPersonSelect = (salesPerson: SalesPerson) => {
    setSelectedSalesPerson(salesPerson)
    setSelectedSlot(null)
  }

  const handleSlotSelect = (slot: Meeting) => {
    lockMeeting(slot).then((isSucceed) => { if (!isSucceed) setSelectedSlot(null); });
    setSelectedSlot(slot);
  }

  const onSlotConfirm = (comment?: string) => {
    if (selectedSlot) {
      bookMeeting(selectedSlot, selectedExhibitor?.origName, comment)
        .then((isSucceed) => {
          if (isSucceed) {
            setSelectedSlot(null);
            setSelectedSalesPerson(null);
            setSelectedExhibitor(null);
            if (selectExhibitorRef.current) {
              selectExhibitorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        });
    }
  }

  const onSlotCancel = () => {
    if (!selectedSlot) return;
    // Release the lock by resetting the selected slot
    unlockMeeting(selectedSlot);
    setSelectedSlot(null);
  }


  const getSlotStatusClass = (status: MeetingStatus) => {
    switch (status) {
      case 'Available':
        return 'slot-available'
      case 'Booked':
        return 'slot-booked'
      case 'Closed':
        return 'slot-closed'
      case 'Updating':
        return 'slot-updating'

    }
  }

  return (
    <>
      <div className="row g-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h3 fw-bold text-dark mb-4">
                1:1 Meeting Scheduler – book your meetings</h2>
              <p className="fw-bold">Dear Delegate,</p>
              <p>
                We are pleased to offer you the opportunity to book meetings with exhibitors at <span className="fst-italic">{user?.eventName}</span> via
                <span className="fw-bold">1:1 Meeting Scheduler</span> –  our new smart meeting scheduling feature.
              </p>

              <ul className="list-unstyled mb-3">
                <li className="d-flex align-items-start mb-1">
                  <i className="bi bi-check2 text-success me-2"></i>
                  <span>Schedule one-on-one meetings with Exhibitors</span>
                </li>
                <li className="d-flex align-items-start mb-1">
                  <i className="bi bi-check2 text-success me-2"></i>
                  <span>Boost and maximize your participation and engagement</span>
                </li>
                <li className="d-flex align-items-start mb-1">
                  <i className="bi bi-check2 text-success me-2"></i>
                  <span>Manage your meetings seamlessly via your registration account</span>
                </li>
              </ul>

              <p>
                Simply select the <span className="fw-bold">exhibitors you wish to meet</span> and{" "}
                <span className="fw-bold">schedule your meetings</span>.
              </p>

              <p className="text-muted fst-italic">
                📅 Limited slots available - book early to secure your appointments!
              </p>
                 <p>Participation in this feature is optional. Only exhibitors who have elected to participate will be included and visible. </p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h3 className="h5 fw-semibold text-dark mb-4">
                Set up
              </h3>

              <div className="row g-3">
                {/* Date Selection */}
                <div className="col-lg-6">
                  <label className="form-label fw-medium text-muted">
                    Select Date
                  </label>
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="form-select"
                  >
                    {exhibitorsMeetingsDates.map((date) => (
                      <option key={date} value={date}>
                        {date}
                      </option>
                    ))}
                  </select>

                </div>

                {/* User Info */}
                <div className="col-lg-6">
                  <label className="form-label fw-medium text-muted">
                    Delegate Information
                  </label>
                  <div className="bg-light p-3 rounded">
                    <p className="small fw-medium text-dark mb-1">
                      {user?.name}
                    </p>
                    <p className="small text-muted mb-0">
                      {user?.email}
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Exhibitors List */}
        <div className="col-12" ref={selectExhibitorRef}>
          <div className="card shadow-sm">
            <div className="card-body">
              <h3 className="h5 fw-semibold text-dark mb-4">
                Available Exhibitors
              </h3>
              <p>Browse the list of exhibitors below and click on the logo of the one you’d like to meet.  </p>

              {/* 🔍 שורת סינון */}
              <div className="d-flex align-items-center justify-content-center gap-2 gap-md-5 m-5b-4 flex-wrap flex-md-nowrap  p-4">

                <input
                  id="filterExhibitors"
                  type="text"
                  className="form-control"
                  placeholder="Filter exhibitors..."
                  value={filterPhrase}
                  onChange={e => setFilterPhrase(e.target.value)}

                />

                <div className="form-check d-flex align-items-center gap-3" style={{ whiteSpace: "nowrap" }}>
                  <input
                    style={{ maxWidth: "250px" }}
                    className="form-check-input"
                    type="checkbox"
                    id="withMeetingsOnly"
                    checked={filterOlyWithMeetings}
                    onChange={e => setFilterOnlyWithMeetings(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="withMeetingsOnly">
                    Available meetings only
                  </label>
                </div>

              </div>


              <div className="row g-3">
                {filteredExhibitors.map((exhibitor) => (
                  <ExhibitorsPreview
                    key={exhibitor.id}
                    exhibitor={exhibitor}
                    selectedDate={selectedDate}
                    handleExhibitorSelect={handleExhibitorSelect}
                    selectedExhibitor={selectedExhibitor ?? undefined}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* message */}
        {selectedExhibitor && (
          <div className="col-12" ref={selectSalesPersonRef}>
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <p> Please check the Events program to find the most suitable time slot before booking your 1:1 Meeting with the Exhibitor.</p>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Sales Persons List */}
        {selectedExhibitor && (
          <div className="col-12" ref={selectSalesPersonRef}>
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4">

                  <h3 className="h5 fw-semibold text-dark mb-4">
                    Select Sales Person - {selectedExhibitor.origName}
                  </h3>
                  <img src={logosMap[selectedExhibitor.id] ?? ''} alt={selectedExhibitor.origName} style={{ maxHeight: '40px', maxWidth: '120px' }} />
                </div>
                <p>The following representative(s) are looking forward to meeting you at the event. Click on the name of the person to see their available slots.  </p>

                <div className="row g-3">
                  {selectedExhibitor.salesPersons.map((salesPerson) => (
                    <SalesPersonsPreview
                      key={salesPerson.id}
                      salesPerson={salesPerson}
                      selectedDate={selectedDate}
                      handleSalesPersonSelect={handleSalesPersonSelect}
                      selectedSalesPerson={selectedSalesPerson ?? undefined}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Time Slots for Selected Sales Person */}
        {selectedSalesPerson && (
          <div className="col-12" ref={selectSlotRef}>
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap" >
                  <h3 className="h5 fw-semibold text-dark mb-0">
                    Time Slots - {selectedSalesPerson.fullName} ({selectedExhibitor?.origName})
                  </h3>
                  <div className="text-muted small">
                    <MeetingTimes selectedSalesPerson={selectedSalesPerson} selectedDate={selectedDate} />

                  </div>
                </div>
                <p> Click on the green box for the time that works best for you.

                  A popup will appear asking you to confirm the time you’ve selected.

                  During this stage, you can add a comment about the specific topic(s) you'd like to discuss with the exhibitor. This helps them prepare relevant information or materials in advance, ensuring a productive and focused conversation during your scheduled time.

                  Once you confirm, your meeting is scheduled!</p>

                <div className="time-slot-grid">
                  {filteredSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => handleSlotSelect(slot)}
                      disabled={slot.status !== 'Available' || isLoaded}
                      className={`time-slot ${getSlotStatusClass(slot.status)} ${slot.status !== 'Available'
                        ? 'disabled'
                        : selectedSlot?.id === slot.id
                          ? 'slot-selected'
                          : ''
                        }`}
                      title={`${formatTime(slot.startTime)} - ${slot.status}`}
                    >
                      <div className="fw-bold">{formatTime(slot.startTime)}</div>
                      <div className="small mt-1">{slot.status}</div>

                    </button>
                  ))}
                </div>

                {/* Legend */}
                <div className="d-flex flex-wrap gap-4 mt-4">
                  <div className="legend-item">
                    <div className="legend-color slot-available"></div>
                    <span>Available</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color slot-booked"></div>
                    <span>Booked</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color slot-closed"></div>
                    <span>Unavailable</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color slot-selected"></div>
                    <span>Selected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Conform and comments */}
      {selectedSlot && selectedSalesPerson && selectedExhibitor && (
        <ConformSlotPopup
          selectedSlot={selectedSlot}
          selectedSalesPerson={selectedSalesPerson}
          selectedExhibitor={selectedExhibitor}
          selectedDate={selectedDate}
          exhibitorLogo={logosMap[selectedExhibitor.id] ?? ''}
          exhibitorProfile={selectedExhibitor.details}
          onSlotConfirm={onSlotConfirm}
          onClose={onSlotCancel}
          isLoaded={isLoaded}
        />
      )}
    </>
  )
}

export default SchedulingMeeting;